import { fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth'

// The imperative half of the auth seam (the React half is AuthContext.tsx —
// see the overview comment there). These functions are called from effects
// and event handlers (e.g. AppInititialisationContainer, branding.tsx), not
// rendered from, so a React context is the wrong shape for them. Instead the
// implementation is a module-level slot: production keeps the Amplify-backed
// default, and demo mode calls setAuthFunctions() at boot (src/demo/index.ts)
// before anything reads it — so nothing here ever touches Cognito in demo.

export type UserPermissions = {
    isAdmin: boolean
    isPlatformAppReviewer: boolean
}

export type AuthFunctions = {
    getUserAttributes: () => Promise<{ email?: string }>
    getUserPermissions: () => Promise<UserPermissions>
}

// Pure so it can be unit tested without mocking Amplify.
export function permissionsFromGroups(groups: unknown): UserPermissions {
    if (!Array.isArray(groups)) {
        return { isAdmin: false, isPlatformAppReviewer: false }
    }
    return {
        isAdmin: groups.includes('admins'),
        isPlatformAppReviewer: groups.includes('platformAppReviewer')
    }
}

const realAuthFunctions: AuthFunctions = {
    getUserAttributes: async () => {
        const attributes = await fetchUserAttributes()
        return { email: attributes.email }
    },
    getUserPermissions: async () => {
        const authSession = await fetchAuthSession()
        // These are Cognito group memberships read from the access token —
        // coarse app-level roles only. Not to be confused with the gateway's
        // getUserPermissions GraphQL query, which serves per-user permissions
        // from the database.
        return permissionsFromGroups(authSession.tokens?.accessToken.payload['cognito:groups'])
    }
}

// Demo mode swaps this before rendering; production never touches it.
let impl: AuthFunctions = realAuthFunctions

export function setAuthFunctions(next: AuthFunctions): void {
    impl = next
}

export function getUserAttributes(): Promise<{ email?: string }> {
    return impl.getUserAttributes()
}

export function getUserPermissions(): Promise<UserPermissions> {
    return impl.getUserPermissions()
}
